import { motion } from "framer-motion";
import { MOODS, type Mood } from "@/lib/moods";
import { X } from "lucide-react";

interface MoodSelectorProps {
  value: string | null;
  onChange: (id: string | null) => void;
}

const MoodPill = ({ m, selected, onClick }: { m: Mood; selected: boolean; onClick: () => void }) => (
  <motion.button
    whileHover={{ scale: 1.04 }}
    whileTap={{ scale: 0.96 }}
    onClick={onClick}
    className={`shrink-0 snap-start rounded-full px-4 py-2 text-sm font-semibold transition-all border ${
      selected
        ? `text-white border-transparent bg-gradient-to-r ${m.gradient} shadow-lg`
        : "border-border bg-card hover:border-primary/40 hover:bg-primary/5 text-foreground"
    }`}
  >
    <span className="mr-1.5">{m.emoji}</span>
    {m.label}
  </motion.button>
);

const MoodSelector = ({ value, onChange }: MoodSelectorProps) => {
  return (
    <section className="mb-8">
      <div className="flex items-end justify-between mb-3 px-1">
        <div>
          <h2 className="font-display text-xl md:text-2xl font-bold">
            What are you in the mood for tonight? <span aria-hidden>💖</span>
          </h2>
          <p className="text-xs text-muted-foreground">Tap a vibe to re-tune your feed.</p>
        </div>
        {value && (
          <button
            onClick={() => onChange(null)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear mood
          </button>
        )}
      </div>
      <div className="-mx-4 px-4 overflow-x-auto snap-x snap-mandatory scrollbar-none">
        <div className="flex gap-2 pb-2">
          {MOODS.map((m) => (
            <MoodPill
              key={m.id}
              m={m}
              selected={value === m.id}
              onClick={() => onChange(value === m.id ? null : m.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default MoodSelector;
